import { PLACEHOLDER_PERMISSION, matchPermission } from '@/lib/permissions';

/**
 * Module-row logic of the Permission Matrix, kept out of the component for the
 * node vitest. Only super_admin may grant or remove the "Show placeholder"
 * permission — the server refuses such a save from anyone else — so for
 * everyone else a module row neither touches it nor counts it. [HW-1061]
 */

type MatrixPermission = { id: string; code: string };

/** A code only super_admin may grant: the placeholder itself, or a row that covers it (`settings.*`, `*`). */
export function isSuperAdminOnlyCode(code: string): boolean {
  return matchPermission(code, PLACEHOLDER_PERMISSION);
}

/** The permissions of a module this user may switch on and off. */
export function editableModulePermissions<T extends MatrixPermission>(
  modulePerms: T[],
  isSuperAdmin: boolean
): T[] {
  return isSuperAdmin ? modulePerms : modulePerms.filter((p) => !isSuperAdminOnlyCode(p.code));
}

/**
 * Ticks or clears a whole module in a role's set: clears it when every
 * editable permission is held, ticks it otherwise. A permission the user may
 * not change is left as it is.
 */
export function toggleModuleInSet(
  set: Set<string>,
  modulePerms: MatrixPermission[],
  isSuperAdmin: boolean
): void {
  const editable = editableModulePermissions(modulePerms, isSuperAdmin);
  if (editable.length === 0) return;
  const allHave = editable.every((p) => set.has(p.id));
  for (const p of editable) {
    if (allHave) set.delete(p.id);
    else set.add(p.id);
  }
}

/**
 * The module row's checkbox state, counting only what this user may change —
 * so a module whose only missing permission is one they cannot grant reads as
 * ticked, and clicking it clears rather than asks for a refused grant. A
 * module with nothing editable shows the state of what it holds.
 */
export function moduleCheckState(
  modulePerms: MatrixPermission[],
  has: (permId: string) => boolean,
  isSuperAdmin: boolean
): { checked: boolean | 'indeterminate'; editable: boolean } {
  const editable = editableModulePermissions(modulePerms, isSuperAdmin);
  const counted = editable.length > 0 ? editable : modulePerms;
  const count = counted.filter((p) => has(p.id)).length;
  const checked = count === 0 ? false : count === counted.length ? true : 'indeterminate';
  return { checked, editable: editable.length > 0 };
}
